/* eslint-disable no-unused-vars */
import {
  dashboardAuthenticationLogin,
  extractAuthToken,
} from "../authenticationService";
import {
  getDashboardApiToken,
  setDashboardApiToken,
} from "../apiSessionService";
import type {
  SaccoWithdrawalsPage,
  CreateStageCollectionPayload,
  CreateStageCollectionResponse,
  StageCollectionsPage,
  TriggerStageCollectionPaymentPayload,
  TriggerStageCollectionPaymentResponse,
  ConfirmStageCollectionPaymentResponse,
} from "../../types/sacco";

export const apiUrl = process.env.REACT_APP_API_URL ?? "";

export function appendHeaders() {
  const token = getDashboardApiToken();
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: "Bearer " + token,
  };
}

export async function fetchWithAuth(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const response = await fetch(input, init);
  if (response.status === 401) {
    await ensureDashboardToken();
    const retryInit = {
      ...init,
      headers: {
        ...(init?.headers ?? {}),
        Authorization: "Bearer " + getDashboardApiToken(),
      },
    };
    return fetch(input, retryInit);
  }
  return response;
}

export function getFaresQuery(faresQuery) {
  return fetchWithAuth(apiUrl + "/v1/fare/query", {
    method: "POST",
    headers: appendHeaders(),
    body: JSON.stringify(faresQuery),
  }).then(function (response) {
    return response.json().then((_res) => {
      return _res;
    });
  });
}

export function getSaccoRoutes(saccoName) {
  return fetchWithAuth(apiUrl + "/v1/sacco", {
    method: "POST",
    headers: appendHeaders(),
    body: JSON.stringify({
      saccoName: saccoName,
    }),
  }).then(function (response) {
    return response.json();
  });
}

export function fetchTrips(saccoName) {
  return fetchWithAuth(apiUrl + "/v1/trips/query/" + saccoName, {
    method: "GET",
    headers: appendHeaders(),
  })
    .then(async function (response) {
      return await response.json();
    })
    .then((_data) => {
      return _data;
    });
}

export function getDetails() {}

export function editFares(editData) {
  return fetchWithAuth(apiUrl + "/v1/fare", {
    method: "POST",
    headers: appendHeaders(),
    body: JSON.stringify(editData),
  }).then(function (response) {
    return response;
  });
}

export function homeSummary(summaryQueryPhone) {
  return fetchWithAuth(apiUrl + "/v1/summary", {
    method: "POST",
    headers: appendHeaders(),
    body: JSON.stringify({
      phone: summaryQueryPhone,
    }),
  }).then(function (response) {
    return response.json();
  });
}

export function fetchRecentTransactions(_user?: unknown) {
  return fetchWithAuth(apiUrl + "/dashboard/recent-transactions", {
    method: "GET",
    headers: appendHeaders(),
  }).then(function (response) {
    return response.json();
  });
}

export function fetchNotifications(
  saccoName,
  page = 0,
  size = 20,
  recipient = "",
  startDate = "",
  endDate = "",
) {
  let _query = "";
  if (recipient) _query += `&recipient=${recipient}`;
  if (startDate) _query += `&startDate=${startDate}`;
  if (endDate) _query += `&endDate=${endDate}`;

  return fetchWithAuth(
    apiUrl +
      `/notification?page=${page}&size=${size}&saccoName=${saccoName}${_query}`,
    {
      method: "GET",
      headers: appendHeaders(),
    },
  )
    .then(async function (response) {
      return await response.json();
    })
    .then((_data) => {
      return _data;
    });
}

// crew management api calls
export function approveOperator(operator) {
  return fetchWithAuth(apiUrl + "/crew/approve", {
    method: "POST",
    headers: appendHeaders(),
    body: JSON.stringify(operator),
  }).then(function (response) {
    return response;
  });
}

export function fetchCrewByPhoneNumber(phoneNumber) {
  return fetchWithAuth(apiUrl + "/crew/query", {
    method: "POST",
    headers: appendHeaders(),
    body: JSON.stringify({ phoneNumber }),
  }).then(function (response) {
    return response.json();
  });
}

export function registerCrewMember(crewMember) {
  return fetchWithAuth(apiUrl + "/crew/register", {
    method: "POST",
    headers: appendHeaders(),
    body: JSON.stringify(crewMember),
  }).then(function (response) {
    return response;
  });
}

export function updateCrewDetails(crewMember) {
  return fetchWithAuth(apiUrl + "/crew/details", {
    method: "PATCH",
    headers: appendHeaders(),
    body: JSON.stringify(crewMember),
  }).then(function (response) {
    return response.json();
  });
}

export function updateaccountsDetails(crewMember) {
  return updateCrewDetails(crewMember);
}

export function registerAccountUser(crewMember) {
  return registerCrewMember(crewMember);
}

export function activateCrewMember(crewMember) {
  return fetchWithAuth(apiUrl + "/crew/post", {
    method: "GET",
    headers: appendHeaders(),
    body: JSON.stringify(crewMember),
  }).then(function (response) {
    return response.json();
  });
}

// crew api calls end

function todayDateParam(): string {
  return new Date().toISOString().slice(0, 10);
}

// parcel service api calls
export function fetchParcels(
  saccoId: string,
  page = 0,
  size = 20,
  recipient = "",
  startDate = "",
  endDate = "",
) {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
  });
  const recipientTrimmed = recipient.trim();
  const start = startDate.trim() || todayDateParam();
  const end = endDate.trim();
  if (recipientTrimmed) params.set("recipient", recipientTrimmed);
  params.set("startDate", start);
  if (end) params.set("endDate", end);

  return fetchWithAuth(
    `${apiUrl}/parcel/${encodeURIComponent(saccoId)}?${params.toString()}`,
    {
      method: "GET",
      headers: appendHeaders(),
    },
  ).then(function (response) {
    return response.json();
  });
}

export async function createNewParcel(parcel, saccoId: string) {
  return fetchWithAuth(apiUrl + `/parcel/register/${saccoId}`, {
    method: "POST",
    headers: appendHeaders(),
    body: JSON.stringify(parcel),
  }).then(function (response) {
    return response;
  });
}

export enum ParcelStatus {
  REGISTERED = "REGISTERED",
  PAID = "PAID",
  IN_TRANSIT = "IN_TRANSIT",
  DELIVERED = "DELIVERED",
  RECEIVED = "RECEIVED",
  CANCELLED = "CANCELLED",
  PAID_CASH = "PAID_CASH",
}

export function updateParcelStatus(
  id: string,
  status: ParcelStatus,
  updatedBy?: string,
) {
  const params = new URLSearchParams();
  const trimmed = updatedBy?.trim();
  if (trimmed) {
    params.set("updated_by", trimmed);
  }
  const query = params.toString();
  const url = `${apiUrl}/parcel/${encodeURIComponent(id)}/${status}${
    query ? `?${query}` : ""
  }`;

  return fetchWithAuth(url, {
    method: "PATCH",
    headers: appendHeaders(),
  }).then(function (response) {
    return response;
  });
}

export function updateParcelDetails(parcel) {
  // console.log("update parcel details: ", parcel);
  return fetchWithAuth(apiUrl + `/parcel/${parcel.id}/${parcel.status}`, {
    method: "PATCH",
    headers: appendHeaders(),
    body: JSON.stringify(parcel),
  }).then(function (response) {
    return response.json();
  });
}

export async function payForParcel(parcel) {
  return fetchWithAuth(apiUrl + "/parcel/pay", {
    method: "POST",
    headers: appendHeaders(),
    body: JSON.stringify(parcel),
  }).then(function (response) {
    return response.json();
  });
}

export const confirmParcelPayment = (paymentCode) => {
  return fetchWithAuth(apiUrl + "/parcel/confirm-payment/" + paymentCode, {
    headers: appendHeaders(),
  }).then((_res) => _res);
};

//parcel api calls end

export function registerMatatu(matatu) {
  return fetchWithAuth(apiUrl + "/crew/register/matatu", {
    method: "POST",
    headers: appendHeaders(),
    body: JSON.stringify(matatu),
  }).then(function (response) {
    return response;
  });
}

export function approveMatatu(matatu) {
  return fetchWithAuth(apiUrl + "/v1/matatu/approve", {
    method: "POST",
    headers: appendHeaders(),
    body: JSON.stringify(matatu),
  }).then(function (response) {
    return response;
  });
}

export function fetchRevenue() {
  return fetchWithAuth(apiUrl + "/dashboard/revenue", {
    method: "GET",
    headers: appendHeaders(),
  }).then(async function (response) {
    return await response.json();
  });
}

export function fetchDaySummary(date) {
  return fetchWithAuth(apiUrl + "/dashboard/summary-by-date", {
    method: "POST",
    headers: appendHeaders(),
    body: JSON.stringify({
      date,
    }),
  }).then(function (response) {
    return response.json();
  });
}

export function fetchMatatuQueue(saccoName) {
  return fetchWithAuth(apiUrl + "/queue/" + saccoName, {
    method: "GET",
    headers: appendHeaders(),
  }).then(function (_res) {
    return _res.json();
  });
}

export async function ensureDashboardToken(maxAttempts = 4) {
  let lastError;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const res = await dashboardLogin();
      const token = extractAuthToken(res);
      if (token) {
        setDashboardApiToken(token);
        return token;
      }
      lastError = new Error("Dashboard login returned no token");
    } catch (e) {
      lastError = e;
    }
    await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
  }
  throw lastError || new Error("Could not obtain API token");
}

// dashboard login (RSA-OAEP encrypted via /authentication/*)
export function dashboardLogin() {
  return dashboardAuthenticationLogin();
}

// withdraw
export function initiateWithdraw(_withdrawal) {
  return fetchWithAuth(apiUrl + "/crew/withdraw", {
    method: "POST",
    headers: appendHeaders(),
    body: JSON.stringify(_withdrawal),
  }).then(function (response) {
    return response.json();
  });
}

// withdraw savings
export function initiateSavingsWithdrawal(_withdrawal) {
  return fetchWithAuth(apiUrl + "/payment/savings/withdraw", {
    method: "POST",
    headers: appendHeaders(),
    body: JSON.stringify(_withdrawal),
  }).then(function (response) {
    return response.json();
  });
}

// savings deposit
export function initiateSavingsDeposit(_deposit) {
  return fetchWithAuth(apiUrl + "/savings/deposit", {
    method: "POST",
    headers: appendHeaders(),
    body: JSON.stringify(_deposit),
  }).then(function (response) {
    return response.json();
  });
}

// fetch wallet details
export function fetchWalletDetails(walletId) {
  return fetchWithAuth(apiUrl + "/wallet/" + encodeURIComponent(walletId), {
    method: "GET",
    headers: appendHeaders(),
  }).then(function (response) {
    return response.json();
  });
}

/** GET /wallet/sacco/:saccoId — balance for a sacco wallet (sacco id from SaccoMembers.sacco). */
export function fetchSaccoWalletDetails(saccoId) {
  return fetchWithAuth(
    apiUrl +
      "/wallet/sacco/" +
      encodeURIComponent(String(saccoId || "").trim()),
    {
      method: "GET",
      headers: appendHeaders(),
    },
  ).then(function (response) {
    return response.json();
  });
}

/** POST /payment/sacco/withdraw */
export function initiateSaccoWithdraw(_withdrawal) {
  return fetchWithAuth(apiUrl + "/payment/sacco/withdraw", {
    method: "POST",
    headers: appendHeaders(),
    body: JSON.stringify(_withdrawal),
  }).then(function (response) {
    return response.json();
  });
}

/** GET /payment/sacco/withdrawal/{sacco_id} — paginated sacco wallet withdrawals */
export function fetchSaccoWithdrawals(
  saccoId: string,
  page = 0,
  size = 20,
): Promise<SaccoWithdrawalsPage> {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
  });
  return fetchWithAuth(
    `${apiUrl}/payment/sacco/withdrawal/${encodeURIComponent(saccoId)}?${params.toString()}`,
    {
      method: "GET",
      headers: appendHeaders(),
    },
  ).then(async (response) => {
    if (!response.ok) {
      let message = `Failed to fetch sacco withdrawals (${response.status})`;
      try {
        const errJson = await response.json();
        message = errJson?.message || errJson?.error || message;
      } catch {
        // keep default message
      }
      throw new Error(message);
    }
    return response.json();
  });
}

/** POST /stage-collection/{sacco_id} — create a new stage collection record. */
export function createStageCollection(
  saccoId: string,
  payload: CreateStageCollectionPayload,
): Promise<CreateStageCollectionResponse> {
  return fetchWithAuth(
    `${apiUrl}/stage-collection/${encodeURIComponent(saccoId)}`,
    {
      method: "POST",
      headers: appendHeaders(),
      body: JSON.stringify(payload),
    },
  ).then(async (response) => {
    if (!response.ok) {
      let message = `Failed to create stage collection (${response.status})`;
      try {
        const errJson = await response.json();
        message = errJson?.message || errJson?.error || message;
      } catch {
        // keep default message
      }
      throw new Error(message);
    }
    return response.json();
  });
}

/** GET /stage-collection/{sacco_id} — paginated stage collections, optional date filters. */
export function fetchStageCollections(
  saccoId: string,
  page = 0,
  size = 20,
  startDate?: string,
  endDate?: string,
  stage?: string,
): Promise<StageCollectionsPage> {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
  });
  if (startDate) params.set("startDate", startDate);
  if (endDate) params.set("endDate", endDate);
  if (stage) params.set("stage", stage);

  return fetchWithAuth(
    `${apiUrl}/stage-collection/${encodeURIComponent(saccoId)}?${params.toString()}`,
    {
      method: "GET",
      headers: appendHeaders(),
    },
  ).then(async (response) => {
    if (!response.ok) {
      let message = `Failed to fetch stage collections (${response.status})`;
      try {
        const errJson = await response.json();
        message = errJson?.message || errJson?.error || message;
      } catch {
        // keep default message
      }
      throw new Error(message);
    }
    return response.json();
  });
}

/** POST /stage-collection/pay/{sacco_id} — trigger payment for a stage collection. */
export function triggerStageCollectionPayment(
  saccoId: string,
  payload: TriggerStageCollectionPaymentPayload,
): Promise<TriggerStageCollectionPaymentResponse> {
  return fetchWithAuth(
    `${apiUrl}/stage-collection/pay/${encodeURIComponent(saccoId)}`,
    {
      method: "POST",
      headers: appendHeaders(),
      body: JSON.stringify(payload),
    },
  ).then(async (response) => {
    if (!response.ok) {
      let message = `Failed to trigger stage collection payment (${response.status})`;
      try {
        const errJson = await response.json();
        message = errJson?.message || errJson?.error || message;
      } catch {
        // keep default message
      }
      throw new Error(message);
    }
    return response.json();
  });
}

/** GET /stage-collection/confirm-payment/{sacco_id}/{payment_code} — confirm payment status. */
export function confirmStageCollectionPayment(
  saccoId: string,
  paymentCode: string,
): Promise<ConfirmStageCollectionPaymentResponse> {
  return fetchWithAuth(
    `${apiUrl}/stage-collection/confirm-payment/${encodeURIComponent(saccoId)}/${encodeURIComponent(paymentCode)}`,
    {
      method: "GET",
      headers: appendHeaders(),
    },
  ).then(async (response) => {
    if (!response.ok) {
      let message = `Failed to confirm stage collection payment (${response.status})`;
      try {
        const errJson = await response.json();
        message = errJson?.message || errJson?.error || message;
      } catch {
        // keep default message
      }
      throw new Error(message);
    }
    return response.json();
  });
}

/** PUT /stage-collection/{collection_id}/{status} — update a stage collection status directly. */
export function updateStageCollectionStatus(
  collectionId: string,
  status: string,
): Promise<void> {
  return fetchWithAuth(
    `${apiUrl}/stage-collection/${encodeURIComponent(collectionId)}/${encodeURIComponent(status)}`,
    {
      method: "PATCH",
      headers: appendHeaders(),
    },
  ).then(async (response) => {
    if (!response.ok) {
      let message = `Failed to update stage collection status (${response.status})`;
      try {
        const errJson = await response.json();
        message = errJson?.message || errJson?.error || message;
      } catch {
        // keep default message
      }
      throw new Error(message);
    }
  });
}

// fetchSavings transactions
export function fetchSavingsTransactions(saccoName, page = 0, size = 20) {
  return fetchWithAuth(
    apiUrl + `/savings/${saccoName}?page=${page}&size=${size}`,
    {
      method: "GET",
      headers: appendHeaders(),
    },
  )
    .then(async function (response) {
      return await response.json();
    })
    .then((_data) => {
      return _data;
    });
}

// fetchSavings transactions by wallet
export function fetchSavingsTransactionsByWallet(
  walletId,
  _saccoName = "",
  page = 0,
  size = 20,
) {
  return fetchWithAuth(
    apiUrl + `/savings/transactions/${walletId}?page=${page}&size=${size}`,
    {
      method: "GET",
      headers: appendHeaders(),
    },
  )
    .then(async function (response) {
      return await response.json();
    })
    .then((_data) => {
      return _data;
    });
}

// fetchActiveSavings wallets
export function fetchactiveSavingsWallets(saccoName, page = 0, size = 20) {
  return fetchWithAuth(
    apiUrl + `/savings/active/wallets?page=${page}&size=${size}`,
    {
      method: "GET",
      headers: appendHeaders(),
    },
  )
    .then(async function (response) {
      return await response.json();
    })
    .then((_data) => {
      return _data;
    });
}

// fetch top routes
export function fetchTopRoutes() {
  return fetchWithAuth(apiUrl + "/dashboard/top-routes", {
    method: "GET",
    headers: appendHeaders(),
  }).then(function (response) {
    return response.json();
  });
}

//loans
export function fetchLoans() {
  return fetchWithAuth(apiUrl + "/loan", {
    method: "GET",
    headers: appendHeaders(),
  }).then(function (response) {
    return response.json();
  });
}

export function getLoanTransactionsById(loanId) {
  return fetchWithAuth(apiUrl + `/loan/transaction/${loanId}`, {
    method: "GET",
    headers: appendHeaders(),
  }).then(function (response) {
    return response.json();
  });
}

export function requestLoan(_deposit) {
  return fetchWithAuth(apiUrl + "/loan/request", {
    method: "POST",
    headers: appendHeaders(),
    body: JSON.stringify(_deposit),
  }).then(function (response) {
    return response.json();
  });
}

export function deductLoans() {
  return fetchWithAuth(apiUrl + "/loan/deduct", {
    method: "POST",
    headers: appendHeaders(),
    body: JSON.stringify({}),
  }).then(function (response) {
    return response.json();
  });
}

export function repayLoan(payload: {
  amount: string;
  walletId: string;
  phoneNumber: string;
}) {
  return fetchWithAuth(apiUrl + "/loan/repay", {
    method: "POST",
    headers: appendHeaders(),
    body: JSON.stringify(payload),
  }).then(function (response) {
    return response.json();
  });
}

export function deductSavings() {
  return fetchWithAuth(apiUrl + "/savings/deposit/batch", {
    method: "GET",
    headers: appendHeaders(),
  }).then(function (response) {
    return response.json();
  });
}

export * from "./saccoApis";
